import React, { useState } from 'react';
import CheckoutHotelRoomTag from './CheckoutHotelRoomTag';
import { KoobAccordion, KoobBadge, sanitizeRichText } from '@koob/ui';
import CheckoutProductFeature from './CheckoutProductFeature';
import { useTranslation } from 'react-i18next';
import CheckoutHotelRoomPrice from './CheckoutHotelRoomPrice';
import CheckoutHotelRoomCard from './CheckoutHotelRoomCard';
import CheckoutHotelRoomSource from './CheckoutHotelRoomSource';
import Carousel, { Modal, ModalGateway } from 'react-images';
import formatHotelRoomTags from '../utilities/formatHotelRoomTags';
import formatHotelRoomName from '../utilities/formatHotelRoomName';
import CheckoutHotelRoomAllotmentAvailable from './CheckoutHotelRoomAllotmentAvailable';

export default function CheckoutHotelRoom({
  scope,
  room,
  parentRoom,
  selectedRoomIndex,
  currentBed,
  price,
  isSelected,
  disabled = false,
  onSelect,
  testId,
  children,
}) {
  const { t } = useTranslation('booking', { keyPrefix: 'room' });

  const [openPicturesModal, setOpenPicturesModal] = useState(false);

  const tags = formatHotelRoomTags(currentBed);

  return (
    <CheckoutHotelRoomCard
      scope={scope}
      room={room}
      parentRoom={parentRoom}
      selectedRoomIndex={selectedRoomIndex}
      travelersCount={price?.travelersCount}
      isSelected={isSelected}
      disabled={disabled}
      onSelect={onSelect}
    >
      <div className="relative flex space-x-5 items-center">
        <div className="self-start w-full text-left">
          <div className="flex justify-between gap-5">
            <div className="flex flex-col place-content-center">
              <div className="flex flex-wrap items-center gap-3">
                <div
                  className={[
                    'text-xl font-semibold',
                    room?.instantAvailability
                      ? 'has-allotment'
                      : 'none-allotment',
                  ].join(' ')}
                  data-testid={testId}
                >
                  {formatHotelRoomName(room, currentBed)}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {tags?.map((tag, index) => (
                    <CheckoutHotelRoomTag
                      key={index}
                      label={tag.label}
                      value={tag.value}
                      startAt={tag.startAt}
                      endAt={tag.endAt}
                    />
                  ))}
                </div>

                {room?.instantAvailability && (
                  <CheckoutHotelRoomAllotmentAvailable
                    scope={scope}
                    room={room}
                  />
                )}
              </div>

              {room?.description && (
                <div
                  className="mt-1 text-gray-500"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichText(room?.description),
                  }}
                />
              )}

              <div className="mt-5 flex flex-wrap gap-3">
                {room?.maxCapacity && (
                  <CheckoutProductFeature icon="fa-users">
                    <div className="flex items-center gap-1.5">
                      <span>{t('capacity')}</span>

                      <KoobBadge>
                        {room?.minCapacity &&
                        room.minCapacity !== room.maxCapacity ? (
                          <>
                            {room.minCapacity}
                            <i className="fal fa-arrows-left-right text-gray-400" />
                            {room.maxCapacity}
                          </>
                        ) : (
                          room.maxCapacity
                        )}
                      </KoobBadge>
                    </div>
                  </CheckoutProductFeature>
                )}
              </div>

              {room?.facilities?.length > 0 && (
                // Card click selects the room — keep the accordion toggle local.
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <KoobAccordion
                    variant="muted"
                    title={`${t('facilities')} (${room.facilities.length})`}
                  >
                    <div className="flex flex-wrap gap-3">
                      {room.facilities.map((facility, index) => (
                        <CheckoutProductFeature
                          key={index}
                          icon="fa-check"
                          iconUrl={facility.iconUrl}
                        >
                          {facility.name}
                        </CheckoutProductFeature>
                      ))}
                    </div>
                  </KoobAccordion>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-3">
                {room?.attachments?.length > 0 && (
                  <div
                    onClick={() => setOpenPicturesModal(true)}
                    className="cursor-pointer"
                  >
                    <KoobBadge color="orange">
                      <i className="far fa-images mr-1.5" />
                      {t('showAllPictures')}
                    </KoobBadge>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 w-full flex-col space-y-3">{children}</div>
        </div>

        <div className="self-stretch flex-shrink-0 w-40 text-right">
          <div className="h-full flex flex-col justify-between space-y-3">
            <CheckoutHotelRoomPrice
              price={price?.total}
              currency={price?.currency}
              travelersCount={price?.travelersCount}
              nightsCount={price?.nightsCount}
            />

            {room?.supplierCode && (
              <div className="flex justify-end">
                <div className="border-2 px-3 py-1 rounded-lg text-sm">
                  <i className="fa fa-link mr-1" /> {room.supplierCode}
                </div>
              </div>
            )}

            {room?.source && (
              <CheckoutHotelRoomSource
                name={room?.source}
                logo={room?.sourceLogo}
              />
            )}
          </div>
        </div>

        <div className="text-xl">
          {disabled ? (
            <i className="far fa-ban text-gray-300" />
          ) : isSelected ? (
            <i className="far fa-check-circle text-k-orange" />
          ) : (
            <i className="far fa-circle text-gray-400" />
          )}
        </div>
      </div>

      <ModalGateway>
        {openPicturesModal && (
          <Modal onClose={() => setOpenPicturesModal(false)}>
            <Carousel
              views={room?.attachments?.map((img) => {
                return {
                  src: img?.url,
                  caption: img?.name,
                };
              })}
            />
          </Modal>
        )}
      </ModalGateway>
    </CheckoutHotelRoomCard>
  );
}
